/**
 * กฎเกี่ยวกับคุณภาพพิกัด — ใช้ร่วมกันระหว่าง UI ที่แสดงผลและ server ที่บังคับใช้
 * server เป็นผู้ตัดสินเสมอ ฝั่ง client ใช้เพื่อให้ผู้ใช้เห็นสถานะทันทีเท่านั้น
 */
import type { GpsSettings } from './config';

export const GPS_QUALITY = ['excellent', 'good', 'fair', 'poor'] as const;
export type GpsQuality = (typeof GPS_QUALITY)[number];

export const GPS_QUALITY_LABELS_TH: Record<GpsQuality, string> = {
  excellent: 'ดีมาก',
  good: 'ดี',
  fair: 'พอใช้',
  poor: 'ยังไม่ผ่าน',
};

export function gpsQuality(accuracyM: number, settings: GpsSettings): GpsQuality {
  if (!Number.isFinite(accuracyM) || accuracyM < 0) return 'poor';
  if (accuracyM <= settings.qualityExcellentM) return 'excellent';
  if (accuracyM <= settings.qualityGoodM) return 'good';
  if (accuracyM <= settings.qualityFairM) return 'fair';
  return 'poor';
}

export function meetsThreshold(accuracyM: number, thresholdM: number): boolean {
  return Number.isFinite(accuracyM) && accuracyM >= 0 && accuracyM <= thresholdM;
}

/**
 * พิกัดเก่าเกินเกณฑ์แล้วหรือยัง — positionTimestamp และ now เป็น epoch ms
 * ยอมให้นาฬิกาเครื่องคลาดจาก server ได้ไม่เกิน 60 วินาที
 * ถ้าคลาดมากกว่านั้นถือว่าเชื่อไม่ได้ ให้วัดใหม่
 */
export function isPositionStale(
  positionTimestamp: number,
  now: number,
  settings: GpsSettings,
): boolean {
  if (!Number.isFinite(positionTimestamp)) return true;
  const ageMs = now - positionTimestamp;
  if (ageMs < -60000) return true;
  return ageMs > settings.maxPositionAgeSeconds * 1000;
}

/** สัดส่วนความคืบหน้า 0-1 สำหรับ progress indicator (ยิ่งแม่นยิ่งเข้าใกล้ 1) */
export function accuracyProgress(accuracyM: number, thresholdM: number): number {
  if (!Number.isFinite(accuracyM) || accuracyM <= 0) return 0;
  if (accuracyM <= thresholdM) return 1;
  // ไล่จาก 0 ที่ 4 เท่าของเกณฑ์ ขึ้นไปเกือบ 1 เมื่อเข้าใกล้เกณฑ์
  const span = thresholdM * 3;
  const over = Math.min(accuracyM - thresholdM, span);
  return Math.max(0, 1 - over / span) * 0.95;
}

export function isValidLatitude(value: number): boolean {
  return Number.isFinite(value) && value >= -90 && value <= 90;
}

export function isValidLongitude(value: number): boolean {
  return Number.isFinite(value) && value >= -180 && value <= 180;
}

/** ปัดพิกัดให้หยาบลงสำหรับผู้ที่มีสิทธิ์จำกัด — 2 ตำแหน่งทศนิยม ประมาณ 1.1 กม. */
export function coarsenCoordinate(value: number, decimals = 2): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
